import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { HeartPulse } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { vibrate } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

export const PRIMARY_BTN =
  "inline-flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-4 px-8 hover:bg-pink-600 hover:scale-[1.02] active:scale-[0.98] transition-transform transition-colors shadow-[0_0_28px_rgba(236,72,153,0.4)] hover:shadow-[0_0_40px_rgba(236,72,153,0.6)]";
export const SECONDARY_BTN =
  "inline-flex items-center justify-center gap-2 rounded-full bg-white/5 text-white font-semibold py-4 px-8 hover:bg-white/10 border border-white/10 transition-colors";

export function CtaButton({ children, testid, event = "view_content", to, secondary = false, className = "" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = () => {
    vibrate();
    trackEvent(event, { location: testid });
    navigate(to || (user ? "/app" : "/onboarding"));
  };
  return (
    <button data-testid={testid} onClick={go} className={`${secondary ? SECONDARY_BTN : PRIMARY_BTN} ${className}`}>
      {children}
    </button>
  );
}

export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function GlowOrb({ className = "", color = "#EC4899", opacity = 0.18 }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute rounded-full blur-[130px] ${className}`}
      style={{ background: color, opacity }}
    />
  );
}

export function PromoBadge({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-pink-500/10 text-pink-400 border border-pink-500/20 text-sm font-semibold tracking-wide">
      <span className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" /> {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, sub, center = false }) {
  return (
    <div className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
      {eyebrow && <p className="text-xs uppercase tracking-[0.25em] text-pink-400 font-bold mb-3">{eyebrow}</p>}
      <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-white">{title}</h2>
      {sub && <p className="mt-4 text-zinc-400 text-base leading-relaxed">{sub}</p>}
    </div>
  );
}

export function BrandMark({ size = 22 }) {
  return (
    <span className="relative inline-flex items-center justify-center">
      <span className="absolute inset-0 rounded-xl bg-pink-500/30 blur-md" aria-hidden />
      <HeartPulse size={size} className="relative text-pink-400" strokeWidth={2.4} aria-label="DocLove" />
    </span>
  );
}
